import { computed, type ComputedRef, type MaybeRefOrGetter, type Ref, toValue } from 'vue'

import type {
  ChartFieldRoleSelection,
  ChartFieldRoleSources,
  ChartType,
  ChartTypeMeta,
  ReportViewType,
} from '../models/chart.types'
import { RENDERABLE_CHART_TYPES } from '../models/chart.types'
import {
  applyFieldRoleSelectionRules,
  getFieldRoleFields,
  toChartFieldCandidates,
} from '../service/chart/fieldRoles'

import { useReportChartFieldState } from './useReportChartFieldState'
import { useReportViewState } from './useReportViewState'
import { useVisualizationTypeCatalog } from './useVisualizationTypeCatalog'

import type { SelectOptionMeta } from '@/shared/ui/select'

export interface UseDataVisibleChartStateOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  defaultView: MaybeRefOrGetter<ReportViewType>
  defaultChartType: MaybeRefOrGetter<ChartType>
  allowedViews?: MaybeRefOrGetter<readonly ReportViewType[] | undefined>
  allowedChartTypes?: MaybeRefOrGetter<readonly ChartType[] | undefined>
  dimensions: MaybeRefOrGetter<readonly string[]>
  metrics: MaybeRefOrGetter<readonly string[]>
  timeDimensions: MaybeRefOrGetter<readonly string[]>
  /**
   * The field-role pickers' full offer pool — e.g. every metric/dimension a report's table can
   * show, same as the table's own customize panel — as opposed to `dimensions`/`metrics`, which
   * stay the chart's *default* field set (used for catalog eligibility and for chart types with no
   * field roles). Falls back to `dimensions`/`metrics` when omitted, so a caller that only ever had
   * one narrow field set keeps its existing behavior.
   */
  fieldSourceDimensions?: MaybeRefOrGetter<readonly string[]>
  fieldSourceMetrics?: MaybeRefOrGetter<readonly string[]>
  dataPointCount?: MaybeRefOrGetter<number | undefined>
  hasComparison?: MaybeRefOrGetter<boolean | undefined>
  hasHierarchy?: MaybeRefOrGetter<boolean | undefined>
  hasPairs?: MaybeRefOrGetter<boolean | undefined>
  fieldLabel: (field: string) => string
  fieldMeta?: (field: string) => SelectOptionMeta | undefined
}

export interface UseDataVisibleChartState {
  catalog: ReturnType<typeof useVisualizationTypeCatalog>
  configuredViews: ComputedRef<ReportViewType[]>
  availableChartTypes: ComputedRef<ChartTypeMeta[]>
  view: Ref<ReportViewType>
  chartType: Ref<ChartType>
  activeMeta: ComputedRef<ChartTypeMeta | null>
  fieldSources: ComputedRef<ChartFieldRoleSources>
  fieldSelection: ReturnType<typeof useReportChartFieldState>['selection']
  fieldsValid: ComputedRef<boolean>
  requestDimensions: ComputedRef<string[]>
  requestMetrics: ComputedRef<string[]>
  validationDimensions: ComputedRef<string[]>
  validationTimeDimensions: ComputedRef<string[]>
  chartCatalogLoading: ComputedRef<boolean>
  chartUnavailable: ComputedRef<boolean>
  viewUnavailable: ComputedRef<boolean>
  setView: (value: ReportViewType) => void
  setChartType: (value: ChartType) => void
  hydrateChartType: (value: string | null) => void
  setFieldSelection: (value: ChartFieldRoleSelection) => void
}

export function unique(fields: readonly string[]): string[] {
  return [...new Set(fields.filter(Boolean))]
}

/** The overwhelming majority of DataVisible surfaces offer both table and chart — callers only
 *  need `allowedViews` to narrow to a subset (e.g. `['chart']`) or add `card`/`cohort`. */
const DEFAULT_VIEWS: readonly ReportViewType[] = ['table', 'chart']

/** Catalog/renderer/field-role intersection for one DataVisible surface. */
export function useDataVisibleChartState(
  options: UseDataVisibleChartStateOptions,
): UseDataVisibleChartState {
  const catalog = useVisualizationTypeCatalog()
  const configuredViews = computed<ReportViewType[]>(() => [
    ...new Set(toValue(options.allowedViews) ?? DEFAULT_VIEWS),
  ])
  const availableChartTypes = computed(() => {
    const allowedTypes = toValue(options.allowedChartTypes)
    const dataPointCount =
      options.dataPointCount === undefined ? undefined : toValue(options.dataPointCount)
    const hasComparison =
      options.hasComparison === undefined ? undefined : toValue(options.hasComparison)
    const hasHierarchy =
      options.hasHierarchy === undefined ? undefined : toValue(options.hasHierarchy)
    const hasPairs = options.hasPairs === undefined ? undefined : toValue(options.hasPairs)
    return catalog.availableChartTypes({
      dimensions: toValue(options.dimensions),
      metrics: toValue(options.metrics),
      timeDimensions: toValue(options.timeDimensions),
      ...(allowedTypes === undefined ? {} : { allowedTypes }),
      ...(dataPointCount === undefined ? {} : { dataPointCount }),
      ...(hasComparison === undefined ? {} : { hasComparison }),
      ...(hasHierarchy === undefined ? {} : { hasHierarchy }),
      ...(hasPairs === undefined ? {} : { hasPairs }),
    })
  })
  const selection = useReportViewState({
    id: options.id,
    partitionName: options.partitionName,
    defaultView: options.defaultView,
    defaultChartType: options.defaultChartType,
    allowedViews: configuredViews,
    availableChartTypes: computed(() => availableChartTypes.value.map((meta) => meta.type)),
    catalogSettled: catalog.settled,
  })
  const activeMeta = computed(
    () => availableChartTypes.value.find((meta) => meta.type === selection.chartType.value) ?? null,
  )
  const fieldSourceDimensions = computed(
    () => toValue(options.fieldSourceDimensions) ?? toValue(options.dimensions),
  )
  const fieldSourceMetrics = computed(
    () => toValue(options.fieldSourceMetrics) ?? toValue(options.metrics),
  )
  const fieldSources = computed<ChartFieldRoleSources>(() => ({
    dimensions: toChartFieldCandidates(
      fieldSourceDimensions.value,
      options.fieldLabel,
      options.fieldMeta,
    ),
    metrics: toChartFieldCandidates(
      fieldSourceMetrics.value,
      options.fieldLabel,
      options.fieldMeta,
    ),
    timeDimensions: toChartFieldCandidates(
      toValue(options.timeDimensions),
      options.fieldLabel,
      options.fieldMeta,
    ),
  }))
  // `fieldSources` above offers every field the report has, so a role with room to spare (e.g. up
  // to 2 categories) would otherwise auto-fill from the whole offer the first time a chart type is
  // opened. `dimensions`/`metrics` are the field set the caller actually intends by default (a
  // section's `chartDimensions: ['channel']`, a saved widget's chart fields, ...); resolving THAT
  // narrower pool gives the seed a fresh chart type hydrates with, while the wider `fieldSources`
  // still lets the picker itself offer — and let the user add — every other report field.
  const defaultFieldSources = computed<ChartFieldRoleSources>(() => ({
    dimensions: toChartFieldCandidates(
      toValue(options.dimensions),
      options.fieldLabel,
      options.fieldMeta,
    ),
    metrics: toChartFieldCandidates(
      toValue(options.metrics),
      options.fieldLabel,
      options.fieldMeta,
    ),
    timeDimensions: toChartFieldCandidates(
      toValue(options.timeDimensions),
      options.fieldLabel,
      options.fieldMeta,
    ),
  }))
  const defaultFieldSelection = computed<ChartFieldRoleSelection>(
    () =>
      applyFieldRoleSelectionRules(activeMeta.value?.fieldRoles ?? [], defaultFieldSources.value)
        .selection,
  )
  const fieldState = useReportChartFieldState({
    id: options.id,
    partitionName: options.partitionName,
    chartType: selection.chartType,
    roles: computed(() => activeMeta.value?.fieldRoles ?? []),
    sources: fieldSources,
    // Cached assignments are scoped to the default dimension set, so a caller that rewrites it
    // (a report section's group picker) re-seeds instead of restoring the previous grouping.
    defaultDimensions: () => toValue(options.dimensions),
    defaultSelection: defaultFieldSelection,
  })
  const roleFields = computed(() => {
    const meta = activeMeta.value
    if (!meta?.fieldRoles.length) {
      return {
        dimensions: [...toValue(options.dimensions)],
        metrics: [...toValue(options.metrics)],
        timeDimensions: [...toValue(options.timeDimensions)],
      }
    }
    return getFieldRoleFields(meta.fieldRoles, fieldState.selection.value)
  })

  return {
    catalog,
    configuredViews,
    availableChartTypes,
    view: selection.view,
    chartType: selection.chartType,
    activeMeta,
    fieldSources,
    fieldSelection: fieldState.selection,
    fieldsValid: fieldState.valid,
    requestDimensions: computed(() =>
      unique([...roleFields.value.dimensions, ...roleFields.value.timeDimensions]),
    ),
    requestMetrics: computed(() => unique(roleFields.value.metrics)),
    validationDimensions: computed(() => unique(roleFields.value.dimensions)),
    validationTimeDimensions: computed(() => unique(roleFields.value.timeDimensions)),
    chartCatalogLoading: computed(() => !catalog.settled.value),
    chartUnavailable: computed(
      () => selection.view.value === 'chart' && catalog.settled.value && activeMeta.value === null,
    ),
    viewUnavailable: computed(() => configuredViews.value.length === 0),
    setView: selection.setView,
    setChartType: selection.setChartType,
    hydrateChartType: (value) => {
      if (
        value &&
        (RENDERABLE_CHART_TYPES as readonly string[]).includes(value) &&
        availableChartTypes.value.some((meta) => meta.type === value)
      ) {
        selection.hydrateChartType(value as ChartType)
      }
    },
    setFieldSelection: fieldState.setSelection,
  }
}
